import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Alert, App, Grid } from 'antd';

import GroupHeader from '../common/GroupHeader';
import { RefreshFailedNotice } from '../common/TabLoadState';
import { DataState, Skeleton, deriveDataState } from '../loading';
import NotificationRow from './NotificationRow';
import { describeChannelReason, resolveChannels } from '../../config/notificationChannels';
import {
	applySavedSettings,
	getNotificationCatalog,
	putNotificationSettings,
	resetNotificationSettings,
} from '../../services/notifications';
import { getUsersByIds } from '../../services/users';
import { apiErrorMessage } from '../../utils/apiErrors';
import { formatCount } from '../../utils/counts';
import { normalizeId } from '../../utils/ids';
import { confirmDestructive } from '../common/confirmDestructive';

// Enough rows for the toolbar and the captions to sit on something while the
// catalog loads: the list is the page, so an empty page under a real toolbar
// would read as "you have none".
const SKELETON_ROWS = 3;

/**
 * ⚠ A ROW'S IDENTITY IS ITS GROUP AND ITS KEY, never the key alone.
 *
 * The account catalog walks every collection the reader can see and pushes one
 * row per (key, collection) into one flat `rows[]`, so the same key legitimately
 * appears several times — a global notification repeats under every collection
 * it reaches, and two collections that fork the same platform notification each
 * hold their own definition under the shared key. `automation_id` is no better:
 * a global definition carries the SAME one in every group.
 *
 * Keyed by `key` alone, one optimistic replace rewrote every sibling with the
 * clicked row's whole object — its group, its name, its audience — and the
 * `groups` memo then re-bucketed them all into one collection and dropped the
 * emptied ones, so a row a reader was looking at simply vanished.
 */
const rowId = row => `${row?.group || ''}:${row?.key || ''}`;

/**
 * THE notification list, at all three tiers.
 *
 * One component takes `tier` ∈ 'user' | 'collection' | 'global' and a
 * `scopeRef`, and every host — the account page, the collection's settings
 * tab, the admin portal — renders this and nothing else. That is the point:
 * a row must read identically wherever it appears while being honest about
 * WHERE its value came from, and two implementations of that promise would
 * have drifted apart by the second bug fix.
 *
 * What differs between tiers is not the row. It is:
 *   - which catalog the API answers with (the service turns the tier into
 *     the scope params);
 *   - which word the `SourceMark` calls "yours" (Mine · This collection ·
 *     Platform);
 *   - where a reset falls back to.
 *
 * The API also decides the GROUPING — collections with a leading Account
 * group at the user tier, categories at the collection tier — so the client
 * never invents a taxonomy it would then have to keep in step with the
 * server's.
 *
 * `find` is a deep link into one row — the inbox drawer's "Mute this
 * notification…" hands over the key (or, where the API sends none, the name),
 * and the row it names is scrolled to and flashed once. A term that matches
 * nothing does nothing at all: the list is still the list, and a page that
 * shouted "not found" over a working list would be worse than silence.
 */
export default function NotificationList({
	tier = 'user',
	scopeRef = null,
	readOnly = false,
	emptyAction = null,
	emptyText,
	find = '',
}) {
	const { message, modal } = App.useApp();
	const screens = Grid.useBreakpoint();
	const compact = screens.md === false;

	const baseId = useId();
	const unavailableHintId = `notification-channels-hint-${baseId}`;
	const readOnlyHintId = `notification-readonly-hint-${baseId}`;

	const collectionId = normalizeId(scopeRef?.collectionId) || null;
	const collectionName = scopeRef?.collectionName || null;

	const [catalog, setCatalog] = useState(null);
	const [loadError, setLoadError] = useState(null);
	const [busy, setBusy] = useState(null); // { id, channel } while one cell is in flight
	const [refused, setRefused] = useState(false); // a write came back 403
	const [announcement, setAnnouncement] = useState('');
	const [usersById, setUsersById] = useState({});

	const load = useCallback(async ({ quiet = false } = {}) => {
		if (!quiet) setLoadError(null);
		try {
			const data = await getNotificationCatalog({ tier, collectionId });
			setCatalog(data);
			setLoadError(null);
		} catch (error) {
			// A failed catalog is an Alert with a Retry, never a blank list:
			// "you have no notifications" and "we could not ask" are different
			// sentences and must not look the same.
			if (!quiet) setCatalog(null);
			setLoadError(error);
		}
	}, [tier, collectionId]);

	useEffect(() => { load(); }, [load]);

	// One batched resolution for every face in the list — the platform's
	// lookup idiom: a list of thirty audiences is one request, not thirty.
	useEffect(() => {
		const ids = [...new Set((catalog?.rows || []).flatMap(row => row?.audience?.userIds || []).filter(Boolean))];
		if (!ids.length) return undefined;
		let active = true;
		getUsersByIds(ids)
			.then(users => {
				if (!active) return;
				setUsersById(Object.fromEntries((users || []).map(user => [normalizeId(user._id), user])));
			})
			.catch(() => {}); // faces are a glance; the sentence beside them still reads
		return () => { active = false; };
	}, [catalog]);

	const channels = useMemo(() => resolveChannels(catalog?.channels), [catalog]);
	const unavailable = channels.filter(channel => !channel.available);
	// Distinct reasons, so two channels that cannot deliver for the same
	// reason say it once — which is the usual case, and the whole point of
	// putting the sentence beside the COLUMN instead of on the cell.
	const unavailableReasons = [...new Set(unavailable.map(channel => channel.reason))];
	const listReadOnly = readOnly || refused;

	const replaceRow = useCallback((id, next) => {
		setCatalog(current => (current
			? { ...current, rows: current.rows.map(row => (rowId(row) === id ? next : row)) }
			: current));
	}, []);

	/**
	 * Every write is optimistic with a rollback — the `PreferencesContext`
	 * idiom verbatim, because a settings switch that waits for a round trip
	 * before it moves reads as broken. The server's answer then REPLACES the
	 * guess rather than confirming it: the merge comes back fully resolved, and
	 * its provenance may be something we could not have predicted (a lock a
	 * tier above, the account master vetoing the channel from somewhere else
	 * entirely).
	 *
	 * ⚠ The answer is `{setting, resolved}`, NOT a catalog row — two different
	 * shapes for one idea, folded together in exactly one place
	 * (`services/notifications.js applySavedSettings`). The old guard here was
	 * `if (saved && saved.key)`, which a `{setting, resolved}` answer simply
	 * fails, so the optimistic guess was kept every time and a value the server
	 * changed never appeared until the page was reloaded.
	 */
	const write = useCallback(async (row, optimistic, request, announce) => {
		const before = row;
		const id = rowId(row);
		replaceRow(id, optimistic);
		try {
			const saved = await request();
			replaceRow(id, applySavedSettings(optimistic, saved));
			setAnnouncement(announce);
		} catch (error) {
			replaceRow(id, before);
			if (error?.response?.status === 403) setRefused(true);
			message.error(apiErrorMessage(error, 'That change did not save. Please try again.'));
		} finally {
			setBusy(null);
		}
	}, [message, replaceRow]);

	/**
	 * ⚠ THE WRITE IS ADDRESSED TO THE ROW'S OWN RUNG, not the list's.
	 *
	 * `collectionId` above is the SCOPE THIS LIST WAS OPENED AT, and on the
	 * account page there is none — the page stands at the account tier and the
	 * catalog answers with rows from every collection the reader is in, one
	 * group per collection. Writing those at the account rung
	 * (`{scope:'user'}`, no collection) was wrong in both directions: the API
	 * resolves a key at the rung you name, so a collection-tier or personal
	 * notification matched nothing there and came back 404 with the switch
	 * springing back, and a GLOBAL-tier one resolved fine and changed that key
	 * in EVERY collection at once — a widening nobody asked for, from a row
	 * shown under one collection's heading.
	 *
	 * So the row's collection (its group's) travels with every write, and the
	 * account rung stays reserved for the one row that really means "all of
	 * them": `MASTER_NOTIFICATION_KEY`, written by `setMasterChannel` and never
	 * by this component.
	 */
	const handleToggle = useCallback((row, rowCollectionId, channelKey, next) => {
		const channel = channels.find(entry => entry.key === channelKey);
		setBusy({ id: rowId(row), channel: channelKey });
		const optimistic = {
			...row,
			channels: {
				...row.channels,
				// Turning it on here makes it yours: the mark moves with the value.
				[channelKey]: { ...(row.channels?.[channelKey] || {}), value: next, source: tier },
			},
		};
		write(
			row,
			optimistic,
			() => putNotificationSettings({ tier, collectionId: rowCollectionId, key: row.key, channels: { [channelKey]: next } }),
			`${channel?.label || channelKey} ${next ? 'on' : 'off'} for ${row.name}.`
		);
	}, [channels, tier, write]);

	const handleMute = useCallback((row, rowCollectionId, next) => {
		const optimistic = { ...row, muted: next, mutedUntil: next ? row.mutedUntil : null };
		write(
			row,
			optimistic,
			// `null` RESETS mutedUntil; leaving the key off would mean "don't
			// touch", and an unmuted row must not keep a stale until-date.
			() => putNotificationSettings({ tier, collectionId: rowCollectionId, key: row.key, muted: next, mutedUntil: next ? undefined : null }),
			next ? `${row.name} muted. It still reaches your inbox.` : `${row.name} unmuted.`
		);
	}, [tier, write]);

	/**
	 * A reset is a DELETE of this tier's row — the `cardLayouts` "Reset to
	 * inherited" idiom. There is no optimistic guess here on purpose: what
	 * the row falls back to is the cascade's answer, not ours, so the list
	 * re-reads rather than inventing a value that might be wrong for a
	 * second and then flick.
	 *
	 * It announces WHAT HAPPENED, not what was asked for. The DELETE answers
	 * `{ok: true, removed: 0}` for a tier that held nothing — the honest answer
	 * to a double-click, and also the answer when the row's value came from a
	 * tier above and there was never anything here to let go of. Saying "reset
	 * to Collection" over an unchanged row would be the list claiming an act it
	 * did not perform.
	 */
	const handleReset = useCallback(async (row, rowCollectionId, target) => {
		try {
			const data = await resetNotificationSettings({ tier, collectionId: rowCollectionId, key: row.key });
			await load({ quiet: true });
			setAnnouncement(data?.removed
				? `${row.name} reset to ${target}.`
				: `Nothing was set here for ${row.name}.`);
		} catch (error) {
			if (error?.response?.status === 403) setRefused(true);
			message.error(apiErrorMessage(error, 'That reset did not save. Please try again.'));
		}
	}, [load, message, tier]);

	// Asked first: the settings made here for this notification go, and the
	// tier above applies again. History restores a reset
	// (`notificationSetting.reset`).
	const confirmReset = useCallback((row, rowCollectionId, target) => {
		confirmDestructive(modal, {
			title: `Reset ${row.name} to ${target}?`,
			content: `What is set here for it is removed and the ${String(target).toLowerCase()} setting applies again. History can restore it.`,
			okText: 'Reset',
			onOk: () => handleReset(row, rowCollectionId, target),
		});
	}, [modal, handleReset]);

	// The API's grouping, in the API's order. A row whose group was not
	// declared still renders — under its own heading at the end — rather than
	// disappearing out of a list somebody is relying on.
	const groups = useMemo(() => {
		const rows = catalog?.rows || [];
		const declared = catalog?.groups || [];
		if (!declared.length) return [{ key: '__all', label: null, rows }];

		const buckets = new Map(declared.map(group => [group.key, { ...group, rows: [] }]));
		const loose = [];
		rows.forEach(row => {
			const bucket = buckets.get(row.group);
			if (bucket) bucket.rows.push(row);
			else loose.push(row);
		});
		const ordered = [...buckets.values()].filter(group => group.rows.length);
		if (loose.length) ordered.push({ key: '__other', label: 'Other', rows: loose });
		return ordered;
	}, [catalog]);

	const total = (catalog?.rows || []).length;

	// The deep-linked row: its key exactly, or its name — both are what a
	// person would have typed into a search for it. One key can now name
	// several rows (one per collection), so the FIRST is the one the flash
	// lands on: the deep link says "here it is", and a list where four rows
	// pulse at once says nothing.
	const foundId = useMemo(() => {
		const term = (find || '').trim().toLowerCase();
		if (!term) return null;
		const rows = catalog?.rows || [];
		const match = rows.find(row => (row?.key || '').toLowerCase() === term)
			|| rows.find(row => (row?.name || '').toLowerCase().includes(term));
		return match ? rowId(match) : null;
	}, [find, catalog]);

	const listRef = useRef(null);
	const [flashId, setFlashId] = useState(null);

	// Scroll it under the reader's eye and flash it once. The flash is a
	// one-shot: it says "here", and a row that kept pulsing would be a row
	// nobody could read.
	useEffect(() => {
		if (!foundId) return undefined;
		// Read the attribute rather than build a selector out of a key that
		// came in from a URL — a scan over thirty rows costs nothing, and
		// nothing has to be escaped.
		const nodes = listRef.current ? [...listRef.current.querySelectorAll('[data-notification-row]')] : [];
		const node = nodes.find(entry => entry.getAttribute('data-notification-row') === foundId);
		if (typeof node?.scrollIntoView === 'function') node.scrollIntoView({ block: 'center', behavior: 'smooth' });
		setFlashId(foundId);
		const timer = setTimeout(() => setFlashId(null), 2400);
		return () => clearTimeout(timer);
	}, [foundId]);

	// ⚠ NO SILENT CAPS. The account catalog walks at most 25 collections and
	// reports what it dropped (`{collections: N}`); until now nothing rendered
	// it, so a person in thirty collections was shown a short list that looked
	// complete — and "this notification does not exist" and "we stopped looking"
	// are not the same sentence. It is a warning rather than an error: the rows
	// that DID come back are all true.
	const droppedCollections = Number(catalog?.truncated?.collections) || 0;

	// The toolbar renders for real while the rows are still coming, so the
	// page does not jump when they land.
	const toolbar = (
		<div className="notification-list__toolbar">
			{droppedCollections ? (
				<Alert
					type="warning"
					showIcon
					className="notification-list__truncated"
					message="This list is not all of them"
					description={
						`Your notifications were gathered from the first 25 collections you can see, `
						+ `so ${formatCount(droppedCollections, 'collection')} further down the list `
						+ `${droppedCollections === 1 ? 'is' : 'are'} not represented here. `
						+ "Open a collection's own notification settings to see and change what it sends you."
					}
				/>
			) : null}
			{unavailable.length ? (
				<p className="form-panel__hint" id={unavailableHintId}>
					{unavailableReasons.map(describeChannelReason).join(' ')}
				</p>
			) : null}
			{listReadOnly ? (
				<p className="form-panel__hint" id={readOnlyHintId}>
					You can see what reaches you here, but changing it is someone else's to do.
				</p>
			) : null}
		</div>
	);

	const captions = (
		// Each cell already names its channel in its own accessible name, so
		// the captions are for the eye only — a screen reader hearing them
		// would hear "Email" twice per row.
		<div className="notification-list__captions" aria-hidden="true">
			<span />
			{compact ? null : <span className="micro-label">Set by</span>}
			<span className="notification-list__caption-channels">
				{channels.map(channel => (
					<span key={channel.key} className="micro-label">{channel.label}</span>
				))}
			</span>
			<span />
		</div>
	);

	return (
		<div className="notification-list" ref={listRef}>
			{toolbar}
			{captions}

			{/* The first read: a list skeleton, or the error with Retry. A
			    failed catalog is never a blank list: "you have no
			    notifications" and "we could not ask" are different sentences. */}
			{catalog === null ? (
				<DataState
					state={deriveDataState({ loading: !loadError, error: loadError ? apiErrorMessage(loadError, 'Refreshing may help.') : null })}
					skeleton={<Skeleton variant="list-row" count={SKELETON_ROWS} />}
					error={loadError ? apiErrorMessage(loadError, 'Refreshing may help.') : null}
					onRetry={() => load()}
					label="notifications"
				/>
			) : null}
			{catalog && loadError ? <RefreshFailedNotice label="notifications" onRetry={() => load()} /> : null}

			{catalog && !total ? (
				<p className="empty-line">
					{emptyText || 'Nothing here notifies you yet.'}
					{emptyAction}
				</p>
			) : null}

			{catalog && total ? groups.map(group => {
				// The collection this whole group belongs to. At the account tier
				// the API groups BY collection and each group names its own; at
				// the collection tier the groups are categories and the scope the
				// list was opened at is the answer for all of them.
				const groupCollectionId = normalizeId(group.collection_id) || collectionId;
				return (
					<section key={group.key} className="notification-list__group">
						{group.label ? (
							<GroupHeader label={group.label} count={group.rows.length} countLabel="notification" />
						) : null}
						<ul className="notification-list__rows">
							{group.rows.map((row, index) => {
								const id = rowId(row);
								return (
									<NotificationRow
										key={id}
										rowId={id}
										row={row}
										tier={tier}
										channels={channels}
										usersById={usersById}
										compact={compact}
										first={index === 0}
										readOnly={listReadOnly}
										busyChannel={busy?.id === id ? busy.channel : null}
										unavailableHintId={unavailable.length ? unavailableHintId : undefined}
										readOnlyHintId={listReadOnly ? readOnlyHintId : undefined}
										flash={id === flashId}
										collectionId={groupCollectionId}
										collectionName={group.collection_id ? group.label : collectionName}
										onToggle={(channelKey, next) => handleToggle(row, groupCollectionId, channelKey, next)}
										onMute={next => handleMute(row, groupCollectionId, next)}
										onReset={target => confirmReset(row, groupCollectionId, target)}
									/>
								);
							})}
						</ul>
					</section>
				);
			}) : null}

			{/* One live region for the whole list: a toggle and a reset say
			    what they did, once, wherever the focus happens to be. */}
			<div className="visually-hidden" aria-live="polite">{announcement}</div>
		</div>
	);
}
